import type { WebinarRoomSubtitleCueDto } from '@/api/webinarRoom'
import { SUBTITLE_LANGUAGES } from '../limits'

/**
 * The recording's subtitles (ORB-1132), drawn by the room itself over the picture, live and on demand: Mux has no
 * captions on a low-latency stream, and the replay must read the same as the session did. What is on screen at a moment,
 * the three ways to draw it, and the viewer's choice of both, kept per browser.
 */

export type SubtitleStyle = 'dark' | 'light' | 'glass'

/** Dark first: the default. Glass keeps a 58% tint under its blur, or white text fails contrast over a white slide. */
export const SUBTITLE_STYLES: readonly { id: SubtitleStyle; label: string }[] = [
    { id: 'dark', label: 'Dark' },
    { id: 'light', label: 'Light' },
    { id: 'glass', label: 'Glass' },
]

/** The text on screen `seconds` into the recording, or null between lines. Lines that overlap show together, in order. */
export function subtitleAt(cues: readonly WebinarRoomSubtitleCueDto[], seconds: number): string | null {
    const showing = cues.filter(cue => cue.StartSeconds <= seconds && seconds < cue.EndSeconds)
    return showing.length === 0 ? null : showing.map(cue => cue.Text).join('\n')
}

/** "English" for "en"; null, said in the console, for a code the room does not offer, which only a drifted API sends. */
export function subtitleLanguageName(code: string): string | null {
    const name = (SUBTITLE_LANGUAGES as Record<string, string>)[code]
    if (name) return name
    console.error(`Subtitles in a language the room does not offer: ${code}`)
    return null
}

export const SUBTITLES_ON_STORAGE_KEY = 'ox-room:subtitles'
export const SUBTITLE_STYLE_STORAGE_KEY = 'ox-room:subtitle-style'

export interface SubtitlePreference {
    on: boolean
    style: SubtitleStyle
}

const isStyle = (value: string | null): value is SubtitleStyle => SUBTITLE_STYLES.some(style => style.id === value)

/** What this browser last chose: off and Dark unless it chose otherwise. Storage that refuses to be read leaves both. */
export function storedSubtitlePreference(storage: Pick<Storage, 'getItem'> | null): SubtitlePreference {
    try {
        const style = storage?.getItem(SUBTITLE_STYLE_STORAGE_KEY) ?? null
        return { on: storage?.getItem(SUBTITLES_ON_STORAGE_KEY) === 'on', style: isStyle(style) ? style : 'dark' }
    } catch {
        return { on: false, style: 'dark' }
    }
}

export function storeSubtitlePreference(storage: Pick<Storage, 'setItem'> | null, preference: SubtitlePreference): void {
    try {
        storage?.setItem(SUBTITLES_ON_STORAGE_KEY, preference.on ? 'on' : 'off')
        storage?.setItem(SUBTITLE_STYLE_STORAGE_KEY, preference.style)
    } catch {
        /* Refused storage: the choice lasts as long as the page. */
    }
}
